
/**
 * TabAddMenu — dropdown triggered by the `+` button in NavHeader.
 *
 * This component includes its own mock menu configuration so it can be
 * consumed directly from the shared design library.
 */

import React, { useEffect, useRef, useCallback, useState, useMemo, useLayoutEffect } from 'react';
import { Button } from '../Button/Button';
import { DialogBox } from '../DialogBox/DialogBox';
import { ShareDisclaimer } from '../ShareDisclaimer/ShareDisclaimer';
import { Popover } from '../Popover/Popover';
import { Icon } from '../Icon/Icon';
import RecentTabsEmptyIcon from '../../../assets/cloud-off-line.svg';
import { Tooltip } from '../Tooltip/Tooltip';

export type TabAddAction =
  | 'new-chat'
  | 'new-workspace'
  | 'new-engagement'
  | 'new-agent'
  | 'close-all';

export interface TabAddMenuItem {
  id: string;
  action: TabAddAction;
  label: string;
  icon: string;
  divider?: boolean;
}

export type RecentTabPopoverActionId =
  | 'pin-toggle'
  | 'share'
  | 'edit'
  | 'rename'
  | 'download'
  | 'delete';

export interface RecentTabPopoverAction {
  id: RecentTabPopoverActionId;
  label: string;
  prefixInstance: string;
}

export interface RecentTabEntry {
  id: string;
  label: string;
  clientLabel?: string;
  groupId?: string | null;
  engId?: string | null;
  timestamp: number;
  tabKind: 'chats' | 'workspaces' | 'engagements' | 'agents' | null;
  isPinned?: boolean;
  pinnedAt?: number | null;
  isOwnedByCurrentUser?: boolean;
  isCustomAgent?: boolean;
  isPublishedCustomAgent?: boolean;
  memberFirmCode?: string | null;
  agentId?: string | null;
  agentName?: string | null;
  isGlobalAgent?: boolean;
  isGlobal?: boolean | null;
  isAgentConversation?: boolean;
}

export type RecentTabActionId = Exclude<RecentTabPopoverActionId, 'pin-toggle'>;

export interface RecentTabActionPayload {
  label?: string;
  recipients?: string[];
  format?: 'xlsx';
  fileName?: string;
  agent?: {
    name: string;
    goal?: string;
    instructions?: string;
    conversationStarters?: string[];
    starterPrompts?: string[];
    model?: string;
    skills?: string[];
    functionality?: string[];
  };
}

export interface RecentTabActionResult {
  status: 'success' | 'error' | 'deferred';
  message?: string;
}

export interface ShareRecipientSearchResult {
  id: string;
  displayName: string;
  email: string;
}

interface TabAddMenuProps {
  open: boolean;
  /** Optional tour target identifier for the rendered menu. */
  tourId?: string;
  onClose: () => void;
  onAction: (action: TabAddAction) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  position: { top: number; left: number } | null;
  /** Optional data loader for menu items. When omitted, a local mock fallback is used. */
  fetchMenuItems?: () => Promise<TabAddMenuItem[]>;
  /** Recently accessed tabs to display below the action items. */
  recentTabs?: RecentTabEntry[];
  /** Callback when user clicks a recent tab entry. */
  onRecentTabSelect?: (entry: RecentTabEntry) => void;
  /** Callback when user pins or unpins a recent tab entry. */
  onRecentTabPinToggle?: (entry: RecentTabEntry, shouldPin: boolean) => void;
  /** Optional resolver for recent-tab kebab actions; defaults to tab-kind based behavior. */
  getRecentTabPopoverActions?: (
    entry: Pick<RecentTabEntry, 'id' | 'label' | 'tabKind' | 'isPinned' | 'isOwnedByCurrentUser' | 'isCustomAgent' | 'isPublishedCustomAgent' | 'agentId' | 'isGlobal' | 'isAgentConversation'>,
  ) => RecentTabPopoverAction[];
  /** Optional action callback to wire actions to host logic/API when available. */
  onRecentTabAction?: (
    entry: RecentTabEntry,
    actionId: RecentTabActionId,
    payload?: RecentTabActionPayload,
  ) => RecentTabActionResult | Promise<RecentTabActionResult>;
  /** Optional directory lookup for Graph-backed share-recipient suggestions. */
  searchShareRecipients?: (query: string) => Promise<ShareRecipientSearchResult[]>;
  /** Optional loader for users who already have an active share for the agent. */
  loadSharedRecipientIds?: (entry: RecentTabEntry) => Promise<string[]>;
  /** Feature flag (applicationSettings.Agents.allowAgentSharing) that gates the Share option. */
  allowAgentSharing?: boolean;
}

const RECENT_TAB_TITLE_MAX_LENGTH = 120;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ShareDialogState {
  isOpen: boolean;
  entry: RecentTabEntry | null;
  draftInput: string;
  recipients: string[];
  recipientNames: Record<string, string>;
  existingRecipientIds: string[];
  isLoadingExistingRecipients: boolean;
  inlineError: string | null;
}
interface TruncatedTooltipLabelProps {
  label: string;
  tooltipTarget: string;
  className: string;
}

function TruncatedTooltipLabel({
  label,
  tooltipTarget,
  className,
}: TruncatedTooltipLabelProps) {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = spanRef.current;
    if (!el) return;

    const check = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    };

    requestAnimationFrame(check);

    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => requestAnimationFrame(check))
        : null;

    observer?.observe(el);

    return () => observer?.disconnect();
  }, [label]);

  return (
    <>
      <span
        ref={spanRef}
        className={className}
        data-target={isTruncated ? tooltipTarget : undefined}
      >
        {label}
      </span>

      {isTruncated && (
        <Tooltip
          tooltipTarget={tooltipTarget}
          tooltipText={label}
          arrow={false}
          arrowPosition="Bottom"
        />
      )}
    </>
  );
}

interface DeleteDialogState {
  isOpen: boolean;
  entry: RecentTabEntry | null;
}

function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function sortRecentTabs(entries: RecentTabEntry[]): RecentTabEntry[] {
  return [...entries].sort((firstEntry, secondEntry) => {
    const firstPinned = Boolean(firstEntry.isPinned);
    const secondPinned = Boolean(secondEntry.isPinned);

    if (firstPinned && secondPinned) {
      const pinOrder = (secondEntry.pinnedAt ?? 0) - (firstEntry.pinnedAt ?? 0);
      if (pinOrder !== 0) return pinOrder;
      return secondEntry.timestamp - firstEntry.timestamp;
    }

    if (firstPinned !== secondPinned) {
      return firstPinned ? -1 : 1;
    }

    return secondEntry.timestamp - firstEntry.timestamp;
  });
}

const AGENT_DASHBOARD_LABEL = 'All Agents Activity';

function getDefaultRecentTabPopoverActions(
  entry: Pick<RecentTabEntry, 'id' | 'label' | 'tabKind' | 'isPinned' | 'isOwnedByCurrentUser' | 'isCustomAgent' | 'isPublishedCustomAgent' | 'agentId' | 'isGlobal' | 'isAgentConversation'>,
  options: { allowAgentSharing?: boolean } = {},
): RecentTabPopoverAction[] {
  const actions: RecentTabPopoverAction[] = [
    {
      id: 'pin-toggle',
      label: entry.isPinned ? 'Unpin' : 'Pin',
      prefixInstance: 'Push Pin',
    },
  ];

  if (entry.label === AGENT_DASHBOARD_LABEL) {
    return actions;
  }

  const isAgentEntry = entry.tabKind === 'agents'
    || entry.id.startsWith('agent:')
    || Boolean(entry.agentId)
    || Boolean(entry.isAgentConversation);

  if (isAgentEntry) {
    if (entry.isGlobal === true || entry.isOwnedByCurrentUser !== true) {
      return actions;
    }

    if (options.allowAgentSharing) {
      actions.push({ id: 'share', label: 'Share', prefixInstance: 'Share' });
    }
    actions.push(
      { id: 'edit', label: 'Edit', prefixInstance: 'Edit' },
      { id: 'delete', label: 'Delete', prefixInstance: 'Delete' },
    );
    return actions;
  }

  if (entry.tabKind === 'chats') {
    actions.push(
      { id: 'rename', label: 'Rename', prefixInstance: 'Edit' },
      { id: 'download', label: 'Download', prefixInstance: 'Download' },
      { id: 'delete', label: 'Delete', prefixInstance: 'Delete' }
    );
  }

  return actions;
}

function sanitizeTabLabel(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function buildXlsxFileName(title: string): string {
  const safeTitle = title
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return `${safeTitle || 'Chat'} .xlsx`.replace(' .xlsx', '.xlsx');
}

function splitEmailInput(value: string): string[] {
  return value
    .split(/[\s,;]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function validateRecipientEmail(email: string): { valid: boolean; reason?: 'invalid' | 'outside-kpmg' } {
  const normalized = email.trim().toLowerCase();
  if (!EMAIL_REGEX.test(normalized)) {
    return { valid: false, reason: 'invalid' };
  }

  if (!normalized.includes('@kpmg.') && !normalized.endsWith('@kpmgdev.com')) {
    return { valid: false, reason: 'outside-kpmg' };
  }

  return { valid: true };
}

function appendRecipients(
  existingRecipients: string[],
  rawInput: string,
  existingSharedRecipients: string[] = [],
): { recipients: string[]; errorText: string | null } {
  const candidates = splitEmailInput(rawInput);
  if (candidates.length === 0) {
    return {
      recipients: existingRecipients,
      errorText: 'Add at least one recipient email address',
    };
  }

  const nextRecipients = [...existingRecipients];
  let errorText: string | null = null;

  for (const rawCandidate of candidates) {
    const candidate = rawCandidate.toLowerCase();
    const validation = validateRecipientEmail(candidate);

    if (!validation.valid) {
      errorText = validation.reason === 'outside-kpmg'
        ? 'This user is unable to be added because they are not in your KPMG member firm.'
        : 'Invalid email address';
      continue;
    }

    if (nextRecipients.includes(candidate) || existingSharedRecipients.includes(candidate)) {
      errorText = 'User already added';
      continue;
    }

    nextRecipients.push(candidate);
  }

  return {
    recipients: nextRecipients,
    errorText,
  };
}

const DEFAULT_TAB_ADD_MENU_ITEMS: TabAddMenuItem[] = [
  {
    id: 'new-chat',
    action: 'new-chat',
    label: 'New Chat',
    icon: 'Add',
  },
];

function mockDelay<T>(data: T, ms = 0): Promise<T> {
  if (ms === 0) {
    return Promise.resolve(data);
  }
  return new Promise((resolve) => setTimeout(() => resolve(data), ms));
}

async function defaultFetchTabAddMenuItems(): Promise<TabAddMenuItem[]> {
  return mockDelay(DEFAULT_TAB_ADD_MENU_ITEMS, 0);
}

export const TabAddMenu = React.memo(function TabAddMenu({
  open,
  tourId,
  onClose,
  onAction,
  triggerRef,
  position,
  fetchMenuItems,
  recentTabs = [],
  onRecentTabSelect,
  onRecentTabPinToggle,
  getRecentTabPopoverActions: resolveRecentTabPopoverActions,
  onRecentTabAction,
  searchShareRecipients,
  loadSharedRecipientIds,
  allowAgentSharing = false,
}: TabAddMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuItems, setMenuItems] = useState<TabAddMenuItem[]>([]);
  const [editingRecentTabId, setEditingRecentTabId] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [editingError, setEditingError] = useState<string | null>(null);
  const [isActionRunning, setIsActionRunning] = useState(false);
  const [shareRecipientSuggestions, setShareRecipientSuggestions] = useState<ShareRecipientSearchResult[]>([]);
  const [isSearchingShareRecipients, setIsSearchingShareRecipients] = useState(false);
  const shareRecipientSearchRequestRef = useRef(0);
  const sharedRecipientsRequestRef = useRef(0);
  const [shareDialog, setShareDialog] = useState<ShareDialogState>({
    isOpen: false,
    entry: null,
    draftInput: '',
    recipients: [],
    recipientNames: {},
    existingRecipientIds: [],
    isLoadingExistingRecipients: false,
    inlineError: null,
  });
  const [shareConfirmationOpen, setShareConfirmationOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    isOpen: false,
    entry: null,
  });

  useEffect(() => {
    const loader = fetchMenuItems ?? defaultFetchTabAddMenuItems;
    loader()
      .then(setMenuItems)
      .catch((error) => {
        console.error('Failed to load tab add menu items', error);
      });
  }, [fetchMenuItems]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const targetElement = e.target;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      if (targetElement instanceof Element && targetElement.closest('.tab-add-menu__recent-more-popover')) return;
      onClose();
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, onClose, triggerRef]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      onClose();
      triggerRef.current?.focus();
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, onClose, triggerRef]);

  const sortedRecentTabs = useMemo(() => sortRecentTabs(recentTabs), [recentTabs]);

  const runRecentTabAction = useCallback(
    async (
      entry: RecentTabEntry,
      actionId: RecentTabActionId,
      payload?: RecentTabActionPayload,
    ): Promise<RecentTabActionResult> => {
      setIsActionRunning(true);
      try {
        if (!onRecentTabAction) {
          return { status: 'deferred' };
        }

        const result = await onRecentTabAction(entry, actionId, payload);
        return result;
      } finally {
        setIsActionRunning(false);
      }
    },
    [onRecentTabAction],
  );

  const startRename = useCallback((entry: RecentTabEntry) => {
    setEditingRecentTabId(entry.id);
    setEditingLabel(entry.label);
    setEditingError(null);
  }, []);

  const cancelRename = useCallback(() => {
    setEditingRecentTabId(null);
    setEditingLabel('');
    setEditingError(null);
  }, []);

  useEffect(() => {
    if (!open) {
      cancelRename();
    }
  }, [cancelRename, open]);

  const submitRename = useCallback(
    async (entry: RecentTabEntry) => {
      const normalizedLabel = sanitizeTabLabel(editingLabel);

      if (!normalizedLabel) {
        setEditingError('Title is required');
        return;
      }

      if (normalizedLabel.length > RECENT_TAB_TITLE_MAX_LENGTH) {
        setEditingError(`Title must be ${RECENT_TAB_TITLE_MAX_LENGTH} characters or less`);
        return;
      }

      const duplicate = recentTabs.some(
        (item) => item.id !== entry.id && item.label.toLowerCase() === normalizedLabel.toLowerCase(),
      );
      if (duplicate) {
        setEditingError('Title already exists');
        return;
      }

      setEditingError(null);
      // Dismiss the input immediately so the rename feels instant; the API
      // call and any rollback on failure happen in the background.
      setEditingRecentTabId(null);
      setEditingLabel('');
      await runRecentTabAction(
        entry,
        'rename',
        { label: normalizedLabel },
      );
    },
    [editingLabel, recentTabs, runRecentTabAction],
  );

  const openShareDialog = useCallback((entry: RecentTabEntry) => {
    const requestId = ++sharedRecipientsRequestRef.current;
    setShareDialog({
      isOpen: true,
      entry,
      draftInput: '',
      recipients: [],
      recipientNames: {},
      existingRecipientIds: [],
      isLoadingExistingRecipients: Boolean(loadSharedRecipientIds),
      inlineError: null,
    });

    loadSharedRecipientIds?.(entry)
      .then((userIds) => {
        if (requestId !== sharedRecipientsRequestRef.current) return;
        setShareDialog((prev) => ({
          ...prev,
          existingRecipientIds: userIds.map((userId) => userId.trim().toLowerCase()),
          isLoadingExistingRecipients: false,
        }));
      })
      .catch(() => {
        if (requestId !== sharedRecipientsRequestRef.current) return;
        setShareDialog((prev) => ({ ...prev, isLoadingExistingRecipients: false }));
      });
  }, [loadSharedRecipientIds]);

  const closeShareDialog = useCallback(() => {
    shareRecipientSearchRequestRef.current += 1;
    sharedRecipientsRequestRef.current += 1;
    setShareRecipientSuggestions([]);
    setIsSearchingShareRecipients(false);
    setShareDialog({
      isOpen: false,
      entry: null,
      draftInput: '',
      recipients: [],
      recipientNames: {},
      existingRecipientIds: [],
      isLoadingExistingRecipients: false,
      inlineError: null,
    });
    setShareConfirmationOpen(false);
  }, []);

  useEffect(() => {
    const query = shareDialog.draftInput.trim();
    if (!shareDialog.isOpen || !searchShareRecipients || query.length < 2) {
      setShareRecipientSuggestions([]);
      setIsSearchingShareRecipients(false);
      return;
    }

    const requestId = ++shareRecipientSearchRequestRef.current;
    const timer = window.setTimeout(() => {
      setIsSearchingShareRecipients(true);
      searchShareRecipients(query)
        .then((results) => {
          if (requestId === shareRecipientSearchRequestRef.current) {
            setShareRecipientSuggestions(results);
          }
        })
        .catch(() => {
          if (requestId === shareRecipientSearchRequestRef.current) {
            setShareRecipientSuggestions([]);
          }
        })
        .finally(() => {
          if (requestId === shareRecipientSearchRequestRef.current) {
            setIsSearchingShareRecipients(false);
          }
        });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [searchShareRecipients, shareDialog.draftInput, shareDialog.isOpen]);

  const openDeleteDialog = useCallback((entry: RecentTabEntry) => {
    setDeleteDialog({
      isOpen: true,
      entry,
    });
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog({
      isOpen: false,
      entry: null,
    });
  }, []);

  const addShareRecipients = useCallback(() => {
    const { recipients, errorText } = appendRecipients(
      shareDialog.recipients,
      shareDialog.draftInput,
      shareDialog.existingRecipientIds,
    );

    setShareDialog((prev) => ({
      ...prev,
      recipients,
      draftInput: '',
      inlineError: errorText,
    }));
  }, [shareDialog.draftInput, shareDialog.existingRecipientIds, shareDialog.recipients]);

  const addShareRecipient = useCallback((user: ShareRecipientSearchResult) => {
    const email = user.email.toLowerCase();
    setShareDialog((prev) => ({
      ...prev,
      ...(() => {
        const { recipients, errorText } = appendRecipients(
          prev.recipients,
          email,
          prev.existingRecipientIds,
        );
        return {
          recipients,
          recipientNames: errorText
            ? prev.recipientNames
            : { ...prev.recipientNames, [email]: user.displayName },
          draftInput: '',
          inlineError: errorText,
        };
      })(),
    }));
    shareRecipientSearchRequestRef.current += 1;
    setShareRecipientSuggestions([]);
  }, []);

  const submitShare = useCallback(async () => {
    if (!shareDialog.entry || shareDialog.inlineError) {
      return;
    }

    const pendingInput = shareDialog.draftInput.trim();
    const merged = pendingInput
      ? appendRecipients(shareDialog.recipients, pendingInput, shareDialog.existingRecipientIds)
      : { recipients: shareDialog.recipients, errorText: null as string | null };

    if (merged.errorText) {
      setShareDialog((prev) => ({
        ...prev,
        recipients: merged.recipients,
        draftInput: '',
        inlineError: merged.errorText,
      }));
      return;
    }

    if (merged.recipients.length === 0) {
      setShareDialog((prev) => ({
        ...prev,
        inlineError: merged.errorText ?? 'Add at least one recipient email address',
      }));
      return;
    }

    if (pendingInput) {
      setShareDialog((prev) => ({
        ...prev,
        recipients: merged.recipients,
        draftInput: '',
        inlineError: merged.errorText,
      }));
    }

    setShareDialog((prev) => ({
      ...prev,
      recipients: merged.recipients,
      draftInput: '',
      inlineError: null,
    }));
    setShareConfirmationOpen(true);
  }, [closeShareDialog, runRecentTabAction, shareDialog.draftInput, shareDialog.entry, shareDialog.existingRecipientIds, shareDialog.recipients]);

  const confirmShare = useCallback(async () => {
    if (!shareDialog.entry || shareDialog.recipients.length === 0) {
      return;
    }

    const result = await runRecentTabAction(
      shareDialog.entry,
      'share',
      { recipients: shareDialog.recipients },
    );

    if (result.status === 'success') {
      closeShareDialog();
      return;
    }

    setShareConfirmationOpen(false);
    setShareDialog((prev) => ({
      ...prev,
      inlineError: result.message ?? 'Unable to share this agent right now. Please try again.',
    }));
  }, [closeShareDialog, runRecentTabAction, shareDialog.entry, shareDialog.recipients]);

  const removeShareRecipient = useCallback((email: string) => {
    setShareDialog((prev) => ({
      ...prev,
      recipients: prev.recipients.filter((item) => item !== email),
      inlineError: null,
    }));
  }, []);

  const submitDelete = useCallback(async () => {
    if (!deleteDialog.entry) {
      return;
    }

    await runRecentTabAction(
      deleteDialog.entry,
      'delete',
      undefined,
    );
    closeDeleteDialog();
  }, [closeDeleteDialog, deleteDialog.entry, runRecentTabAction]);

  const handleRecentPinToggle = (entry: RecentTabEntry) => {
    onRecentTabPinToggle?.(entry, !entry.isPinned);
  };

  const executeAction = useCallback(
    async (entry: RecentTabEntry, actionId: RecentTabPopoverActionId) => {
      if (actionId === 'pin-toggle') {
        handleRecentPinToggle(entry);
        return;
      }

      if (actionId === 'rename') {
        startRename(entry);
        return;
      }

      if (actionId === 'share') {
        if (entry.label === AGENT_DASHBOARD_LABEL) {
          return;
        }

        openShareDialog(entry);
        return;
      }

      if (actionId === 'download') {
        await runRecentTabAction(
          entry,
          'download',
          { format: 'xlsx', fileName: buildXlsxFileName(entry.label) },
        );
        return;
      }

      if (actionId === 'edit' || actionId === 'delete') {
        if (entry.tabKind === 'agents' && entry.isOwnedByCurrentUser === false) {
          return;
        }

        if (actionId === 'edit') {
          await runRecentTabAction(
            entry,
            'edit',
            undefined,
          );
          return;
        }

        openDeleteDialog(entry);
      }
    },
    [handleRecentPinToggle, openDeleteDialog, openShareDialog, runRecentTabAction, startRename],
  );

  if (!open || !position) return null;

  const handle = (action: TabAddAction) => {
    onAction(action);
    onClose();
  };

  const handleRecentClick = (entry: RecentTabEntry) => {
    onRecentTabSelect?.(entry);
    onClose();
  };

  const closeRecentActionsPopover = (event: React.MouseEvent<HTMLElement>) => {
    const overlayRoot = event.currentTarget.closest('[data-pc-name="overlaypanel"]') as HTMLElement | null;
    const closeButton = overlayRoot?.querySelector('[data-pc-section="closebutton"]') as HTMLElement | null;

    if (closeButton) {
      closeButton.click();
      return;
    }

    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
  };

  return (
    <div
      ref={menuRef}
      className="tab-add-menu"
      data-tour-id={tourId}
      style={{ top: `${position.top}px`, left: `${position.left}px` }}
    >
      {menuItems.map((item) => {
        if (item.divider) {
          return <div key={item.id} className="tab-add-menu__divider" />;
        }

        return (
          <Button
            key={item.id}
            buttonClass="tab-add-menu__option"
            buttonPrefix
            buttonType="Secondary"
            classType="Standard"
            label={item.label}
            prefixInstance={item.icon}
            size="Medium"
            state="Default"
            text
            theme="Rectangular"
            data-tab-action={item.action}
            onClick={() => handle(item.action)}
          />
        );
      })}

      <div className="tab-add-menu__divider" />
      <div className="tab-add-menu__section-header">RECENT TABS</div>
      {recentTabs.length === 0 ? (
        <div className="tab-add-menu__no-recent">
          <div className="tab-add-menu__no-recent-icon">
            <img src={RecentTabsEmptyIcon} alt="No Recent Tabs" />
          </div>
          <span>No Recent Tabs</span>
        </div>
      ) : (
        <div className="tab-add-menu__recent-tabs-list">
          {sortedRecentTabs.map((entry) => {
            const isPinned = Boolean(entry.isPinned);
            const popoverActions = resolveRecentTabPopoverActions
              ? resolveRecentTabPopoverActions(entry)
              : getDefaultRecentTabPopoverActions(entry, { allowAgentSharing });

            return (
              <div
                key={`recent-${entry.id}`}
                className={`tab-add-menu__option tab-add-menu__option--recent${isPinned ? ' tab-add-menu__option--recent-pinned' : ''}`}
              >
                <button type="button" onClick={() => handleRecentClick(entry)} disabled={editingRecentTabId === entry.id}>
                  <div className="tab-add-menu__recent-info">
                    <span className="tab-add-menu__recent-label-wrap">
                      {editingRecentTabId === entry.id ? (
                        <span className="tab-add-menu__recent-rename-wrap" onClick={(event) => event.stopPropagation()}>
                          <input
                            className="tab-add-menu__recent-rename-input"
                            value={editingLabel}
                            autoFocus
                            maxLength={RECENT_TAB_TITLE_MAX_LENGTH}
                            onChange={(event) => setEditingLabel(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                void submitRename(entry);
                              }
                              if (event.key === 'Escape') {
                                event.preventDefault();
                                cancelRename();
                              }
                            }}
                            onBlur={() => {
                              if (editingRecentTabId === entry.id) {
                                cancelRename();
                              }
                            }}
                          />
                          {editingError && <span className="tab-add-menu__recent-rename-error">{editingError}</span>}
                        </span>
                      ) : (
                        <TruncatedTooltipLabel
                          label={entry.label}
                          tooltipTarget={`recent-tab-label-${entry.id}`}
                          className="tab-add-menu__recent-label"
                        />
                      )}
                    </span>
                    <span className="tab-add-menu__recent-meta">
                      {entry.clientLabel && (
                        <span className="tab-add-menu__recent-workspace">{entry.clientLabel}</span>
                      )}
                      <span className="tab-add-menu__recent-time">{formatTimestamp(entry.timestamp)}</span>
                    </span>
                  </div>
                </button>
                <div className="tab-add-menu__recent-actions">
                  <Button
                    buttonType="Tertiary"
                    label={<span className="material-symbols-outlined">keep</span>}
                    classType="Standard"
                    size="Medium"
                    state="Default"
                    text
                    theme="Rectangular"
                    buttonClass='tab-add-menu__recent-pin-icon'
                  />
                  <div className="tab-add-menu__recent-more">
                    <Popover
                      content={
                        <div
                            className="tab-add-menu__recent-more-content"
                            onMouseDown={(event) => event.stopPropagation()}
                        >
                          {popoverActions.map((actionItem) => (
                            <Button
                              key={`${entry.id}-${actionItem.id}`}
                              buttonPrefix
                              buttonType="Tertiary"
                              classType="Standard"
                              label={actionItem.label}
                              onClick={(event) => {
                                void executeAction(entry, actionItem.id);
                                closeRecentActionsPopover(event);
                              }}
                              prefixInstance={actionItem.prefixInstance}
                              size="Medium"
                              state="Default"
                              text
                              theme="Rectangular"
                            />
                          ))}
                        </div>
                      }
                      id={`popover-id-${entry.id}`}
                      kebabMenu
                      positioning="top-right"
                      triggerElement={
                        <Icon
                          name="Kebab Menu Vertical"
                          shape="Square"
                          size="Medium"
                          type="Base Icon"
                          variant="Outline"
                        />
                      }
                      popoverCustomClass="tab-add-menu__recent-more-popover"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {shareDialog.isOpen && !shareConfirmationOpen && (
        <div className="tab-add-menu__modal-backdrop" role="presentation" onMouseDown={closeShareDialog}>
          <div
            className="tab-add-menu__modal tab-add-menu__modal--share"
            role="dialog"
            aria-modal="true"
            aria-label="Share agent"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="tab-add-menu__modal-header">
              <h3>Share</h3>
              <button type="button" onClick={closeShareDialog} aria-label="Close share dialog">×</button>
            </div>
            <div className="tab-add-menu__modal-content">
              <p className="tab-add-menu__modal-subtitle">
                Share this agent with your team
              </p>
              <div
                className="tab-add-menu__share-agent-title"
                aria-label="Agent title being shared"
              >
                {shareDialog.entry?.label ?? ''}
              </div>
              <div className="tab-add-menu__share-input-shell">
                <input
                  id="recent-tab-share-input"
                  className="tab-add-menu__modal-input tab-add-menu__modal-input--share"
                  value={shareDialog.draftInput}
                  disabled={shareDialog.isLoadingExistingRecipients}
                  onChange={(event) => setShareDialog((prev) => ({ ...prev, draftInput: event.target.value, inlineError: null }))}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
                      event.preventDefault();
                      addShareRecipients();
                    }
                  }}
                  placeholder="Search for a member to add"
                />
                {(isSearchingShareRecipients || shareRecipientSuggestions.length > 0) && (
                  <div className="tab-add-menu__share-suggestions" role="listbox" aria-label="Member search results">
                    {isSearchingShareRecipients && <span className="tab-add-menu__share-searching">Searching members...</span>}
                    {shareRecipientSuggestions.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        className="tab-add-menu__share-suggestion"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => addShareRecipient(user)}
                        role="option"
                        aria-label={`Add ${user.displayName}`}
                      >
                        <span>{user.displayName}</span>
                        <span>{user.email}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {shareDialog.inlineError && (
                <p className="tab-add-menu__field-error">{shareDialog.inlineError}</p>
              )}
              {shareDialog.recipients.length > 0 && (
                <div className="tab-add-menu__share-selected-members">
                  <p className="tab-add-menu__share-selected-count">
                    Shared with {shareDialog.recipients.length} member{shareDialog.recipients.length === 1 ? '' : 's'}
                  </p>
                  <div className="tab-add-menu__recipient-chips">
                    {shareDialog.recipients.map((email) => {
                      const displayName = shareDialog.recipientNames[email] ?? email;
                      return (
                        <button
                          key={email}
                          type="button"
                          className="tab-add-menu__recipient-chip"
                          onClick={() => removeShareRecipient(email)}
                          title={`Remove ${displayName}`}
                        >
                          <span className="tab-add-menu__recipient-initial" aria-hidden="true">
                            {displayName.charAt(0).toUpperCase()}
                          </span>
                          <span>{displayName}</span>
                          <span aria-hidden="true">×</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="tab-add-menu__modal-actions">
              <Button
                buttonType="Tertiary"
                classType="Standard"
                label="Cancel"
                size="Medium"
                state="Default"
                text
                theme="Rectangular"
                onClick={closeShareDialog}
              />
              <Button
                buttonType="Primary"
                classType="Standard"
                label={isActionRunning ? 'Sharing...' : 'Share'}
                size="Medium"
                state={isActionRunning || shareDialog.isLoadingExistingRecipients || Boolean(shareDialog.inlineError) ? 'Disabled' : 'Default'}
                text
                theme="Rectangular"
                onClick={() => {
                  void submitShare();
                }}
              />
            </div>
          </div>
        </div>
      )}

      {shareConfirmationOpen && shareDialog.entry && (
        <DialogBox
          open
          title="Confirm Sharing Action"
          subtitle="You are about to share this content with the selected user(s)."
          size="md"
          variant="default"
          disableOverlayClose
          overlayClassName="tab-add-menu__share-confirmation-overlay"
          actions={[
            {
              label: 'Cancel',
              variant: 'secondary',
              onClick: () => setShareConfirmationOpen(false),
            },
            {
              label: isActionRunning ? 'Sharing...' : 'Confirm and Share',
              variant: 'primary',
              disabled: isActionRunning,
              onClick: () => void confirmShare(),
            },
          ]}
        >
          <ShareDisclaimer />
        </DialogBox>
      )}

      {deleteDialog.isOpen && deleteDialog.entry && (
        <div className="tab-add-menu__modal-backdrop" role="presentation" onMouseDown={closeDeleteDialog}>
          <div
            className="tab-add-menu__modal"
            role="dialog"
            aria-modal="true"
            aria-label={deleteDialog.entry.tabKind === 'agents' ? 'Remove agent' : 'Session delete confirmation'}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="tab-add-menu__modal-header">
              <h3>{deleteDialog.entry.tabKind === 'agents' ? 'Remove Agent' : 'Session Delete Confirmation'}</h3>
              <button type="button" onClick={closeDeleteDialog} aria-label="Close delete dialog">×</button>
            </div>
            <div className="tab-add-menu__modal-content">
              <p className="tab-add-menu__modal-subtitle">
                {deleteDialog.entry.tabKind === 'agents'
                  ? 'Removing this agent will remove your access and cannot be undone. Are you sure you want to proceed?'
                  : 'This will delete this chat session from your chat history. Are you sure you want to proceed?'}
              </p>
            </div>
            <div className="tab-add-menu__modal-actions">
              <Button
                buttonType="Tertiary"
                classType="Standard"
                buttonClass="tab-add-menu__cancel-button"
                label={deleteDialog.entry.tabKind === 'agents' ? 'No, Cancel' : 'Cancel'}
                size="Medium"
                state="Default"
                text
                theme="Rectangular"
                onClick={closeDeleteDialog}
              />
              <Button
                buttonType="Primary"
                classType="Standard"
                buttonClass="tab-add-menu__delete-button"
                label={isActionRunning ? 'Removing...' : (deleteDialog.entry.tabKind === 'agents' ? 'Yes, Remove' : 'Delete')}
                size="Medium"
                state={isActionRunning ? 'Disabled' : 'Default'}
                text
                theme="Rectangular"
                onClick={() => {
                  void submitDelete();
                }}
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
});

export default TabAddMenu;